import React, { useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ChangelogEntry, fetchChangelog } from '../api/changelog';
import { useAuth } from '../services/AuthContext';
import { C, FONT, hexToRgba, lcarsLabelSx } from '../theme/tokens';

/**
 * Shows the newest changelog entry once per version, mounted once in
 * Layout.tsx so it appears over whatever page a member lands on first.
 *
 * Deliberately plain: no boot animation, no scanline texture. Every other
 * dialog in this app (FittingDialog, the vault's entry editors) is a bare
 * MUI Dialog with this app's own tokens, and a changelog popup earning a
 * bespoke motion system while nothing else has one would make it the odd
 * one out rather than a polish pass.
 */
export const WHATS_NEW_KEY = 'wh-gobbo:lastSeenChangelogVersion';

function markVersionSeen(version: string) {
  localStorage.setItem(WHATS_NEW_KEY, version);
}

// A DOM event rather than a context: the dialog is mounted app-wide and the
// only other caller is the NavBar's bell icon, so there's no state to share,
// just a poke to reopen even once the current version is marked seen.
const OPEN_EVENT = 'whatsnew:open';
export function openWhatsNew() {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

const WhatsNewDialog: React.FC = () => {
  const { me } = useAuth();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);
  const [forced, setForced] = useState(false);

  const { data: entries } = useQuery({
    queryKey: ['changelog'],
    queryFn: fetchChangelog,
    enabled: me.isMember,
    staleTime: Infinity,
  });

  useEffect(() => {
    const open = () => {
      setDismissed(false);
      setForced(true);
    };
    window.addEventListener(OPEN_EVENT, open);
    return () => window.removeEventListener(OPEN_EVENT, open);
  }, []);

  const latest = entries?.[0];
  // Plain !== rather than a semver compare: a rollback re-showing the dialog
  // once is harmless, and every version string here is hand-picked anyway.
  const unseen = !!latest && localStorage.getItem(WHATS_NEW_KEY) !== latest.version;
  const visible = !!latest && !dismissed && (unseen || forced);

  if (!visible || !latest) return null;

  const close = () => {
    markVersionSeen(latest.version);
    setDismissed(true);
    setForced(false);
  };

  return (
    <Dialog open onClose={close} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <NewReleasesIcon sx={{ color: C.gold }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontFamily: FONT.display, fontWeight: 700, fontSize: '1.05rem', color: C.text }}>
            What&rsquo;s new
          </Typography>
          <Typography sx={{ color: C.textDim, fontFamily: FONT.mono, fontSize: '0.72rem' }}>
            v{latest.version}
            {latest.date ? ` · ${latest.date}` : ''} — {latest.title}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ borderColor: C.hairline }}>
        <ChangelogGroups entry={latest} />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button
          size="small"
          onClick={() => {
            close();
            navigate('/whats-new');
          }}
          sx={{ color: C.textDim, mr: 'auto' }}
        >
          Full history &rarr;
        </Button>
        <Button size="small" variant="contained" color="secondary" onClick={close}>
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** One entry's groups, each a small label over its own bullet list — shared
 * between the popup (just the newest entry) and the full-history page (every
 * entry, one of these per version). */
export const ChangelogGroups: React.FC<{ entry: ChangelogEntry }> = ({ entry }) => (
  <Stack spacing={2}>
    {entry.groups.map((g) => (
      <Box key={g.heading}>
        <Typography sx={{ ...lcarsLabelSx(C.gold, '0.68rem'), mb: 0.75 }}>{g.heading}</Typography>
        <Stack component="ul" spacing={0.6} sx={{ m: 0, pl: 2 }}>
          {g.bullets.map((b, i) => (
            <Typography
              key={i}
              component="li"
              sx={{ color: C.text, fontSize: '0.85rem', lineHeight: 1.55, '&::marker': { color: hexToRgba(C.gold, 0.7) } }}
            >
              {b}
            </Typography>
          ))}
        </Stack>
      </Box>
    ))}
  </Stack>
);

export default WhatsNewDialog;
